import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import supabase from '../../utils/supabase';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';

import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import JobsTable from './components/JobsTable';
import ApplicationsGrid from './components/ApplicationsGrid';
import HiredVAsList from './components/HiredVAsList';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import QuickActions from './components/QuickActions';
import EnhancedInterviewScheduleModal from '../../components/interview/EnhancedInterviewScheduleModal';
import employerService from '../../utils/employerService';
import { SupabaseError } from '../../utils/errorHandling';
import supabaseService from '../../services/supabaseService';
import { getHiredVAs, endContract } from '../../services/vaService';
import cacheService from '../../services/cacheService';
import {
  JobsTableSkeleton,
  ApplicationsGridSkeleton,
  HiredVAsListSkeleton,
  PerformanceAnalyticsSkeleton,
  FormsTableSkeleton
} from './components/EmployerDashboardTabSkeletons';
import FormsTable from './components/FormsTable';
import formService from '../../services/formService';

const EmployerDashboard = () => {
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { canPerformAction, recordAction } = useSubscriptionLimits();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Get active tab and job filter from URL or default to 'jobs'
  const urlParams = new URLSearchParams(location.search);
  const activeTab = urlParams.get('tab') || 'jobs';
  const jobFilter = urlParams.get('jobId') || null;

  // Data states
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [hiredVAs, setHiredVAs] = useState([]);
  const [forms, setForms] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Individual loading states for each tab
  const [loadingTabs, setLoadingTabs] = useState({
    jobs: true,
    applications: true,
    hired: true,
    forms: true,
    analytics: true
  });

  // Interview scheduling modal state
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedApplicationForInterview, setSelectedApplicationForInterview] = useState(null);

  // Sidebar and view states
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Load dashboard data
  useEffect(() => {
    if (user?.id && userProfile?.role === 'employer') {
      loadDashboardData();
    }
  }, [user?.id, userProfile?.role]);

  // Real-time subscription: listen for new/updated job applications
  useEffect(() => {
    if (!user?.id || userProfile?.role !== 'employer') return;

    const channel = supabase
      .channel(`employer_dashboard_applications_${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'job_applications' },
        async (payload) => {
          const newApp = payload.new;

          // Verify this application belongs to one of the employer's jobs
          const { data: job } = await supabase
            .from('jobs')
            .select('id, title')
            .eq('id', newApp.job_id)
            .eq('employer_id', user.id)
            .single();

          if (!job) return;

          // Fetch VA profile for the new applicant
          const { data: applicant } = await supabase
            .from('user_profiles')
            .select('id, full_name, bio, profile_picture, experience_years, hourly_rate, skills, location, email')
            .eq('id', newApp.applicant_id)
            .single();

          const transformed = {
            id: newApp.id,
            applicant_id: newApp.applicant_id,
            job_id: newApp.job_id,
            name: applicant?.full_name || 'Unknown',
            title: applicant?.bio?.split('.')[0] || 'Virtual Assistant',
            avatar: applicant?.profile_picture || null,
            matchPercentage: newApp.match_percentage || 0,
            rating: 4.5,
            experience: applicant?.experience_years || 0,
            hourlyRate: applicant?.hourly_rate || 0,
            proposedRate: newApp.proposed_rate || applicant?.hourly_rate || 0,
            coverLetter: newApp.cover_letter || '',
            skills: applicant?.skills || [],
            location: applicant?.location || 'Unknown',
            appliedAt: newApp.applied_at
              ? new Date(newApp.applied_at).toLocaleDateString()
              : 'Just now',
            status: newApp.status,
            jobTitle: job.title,
            employerNotes: newApp.employer_notes || '',
            interviewScheduledAt: newApp.interview_scheduled_at || null,
            match_breakdown: newApp.match_breakdown || null
          };

          setApplications(prev => [transformed, ...prev]);

          // Invalidate cached dashboard stats so next mount gets fresh data
          cacheService.invalidateUserAnalytics?.(user.id);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'job_applications' },
        async (payload) => {
          const updatedApp = payload.new;

          // Only update applications already present in state (belongs to this employer)
          setApplications(prev => {
            const exists = prev.find(a => a.id === updatedApp.id);
            if (!exists) return prev;
            return prev.map(a =>
              a.id === updatedApp.id
                ? {
                    ...a,
                    status: updatedApp.status,
                    employerNotes: updatedApp.employer_notes || a.employerNotes,
                    interviewScheduledAt: updatedApp.interview_scheduled_at || a.interviewScheduledAt,
                    matchPercentage: updatedApp.match_percentage ?? a.matchPercentage
                  }
                : a
            );
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, userProfile?.role]);

  // Load inactive tabs in background after active tab is loaded
  useEffect(() => {
    if (!loading && user?.id) {
      // Load other tabs that are not currently active
      const backgroundLoad = async () => {
        if (activeTab !== 'jobs' && loadingTabs.jobs) {
          await loadTabData('jobs');
        }
        if (activeTab !== 'applications' && loadingTabs.applications) {
          await loadTabData('applications');
        }
        if (activeTab !== 'hired' && loadingTabs.hired) {
          await loadTabData('hired');
        }
        if (activeTab !== 'forms' && loadingTabs.forms) {
          await loadTabData('forms');
        }
        if (activeTab !== 'analytics' && loadingTabs.analytics) {
          await loadTabData('analytics');
        }
      };

      // Delay background loading slightly to prioritize active tab
      const timer = setTimeout(backgroundLoad, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, activeTab, user?.id, loadingTabs]);

  // Memoized search filtering for better performance
  const { filteredJobs, filteredApplications, filteredHiredVAs, filteredForms } = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        filteredJobs: jobs,
        filteredApplications: applications,
        filteredHiredVAs: hiredVAs,
        filteredForms: forms
      };
    }

    const query = searchQuery.toLowerCase().trim();

    const searchedJobs = jobs.filter(job => 
      job.title?.toLowerCase().includes(query) ||
      job.description?.toLowerCase().includes(query) ||
      job.required_skills?.some(skill => skill.toLowerCase().includes(query)) ||
      job.location?.toLowerCase().includes(query)
    );

    const searchedApplications = applications.filter(app => 
      app.name?.toLowerCase().includes(query) ||
      app.title?.toLowerCase().includes(query) ||
      app.jobTitle?.toLowerCase().includes(query) ||
      (Array.isArray(app.skills) && app.skills.some(skill => skill.toLowerCase().includes(query))) ||
      app.location?.toLowerCase().includes(query)
    );

    const searchedHiredVAs = hiredVAs.filter(va => {
      if (!va) return false;
      
      const fullNameMatch = va.full_name?.toLowerCase().includes(query);
      const titleMatch = va.title?.toLowerCase().includes(query);
      const bioMatch = va.bio?.toLowerCase().includes(query);
      
      let skillsMatch = false;
      if (Array.isArray(va.skills)) {
        skillsMatch = va.skills.some(skill => {
          if (typeof skill === 'string') {
            return skill.toLowerCase().includes(query);
          }
          if (typeof skill === 'object' && skill?.name) {
            return skill.name.toLowerCase().includes(query);
          }
          return false;
        });
      }
      
      const locationMatch = va.location?.toLowerCase().includes(query);
      return fullNameMatch || titleMatch || bioMatch || skillsMatch || locationMatch;
    });

    const searchedForms = forms.filter(form =>
      form.title?.toLowerCase().includes(query) ||
      form.description?.toLowerCase().includes(query)
    );

    return {
      filteredJobs: searchedJobs,
      filteredApplications: searchedApplications,
      filteredHiredVAs: searchedHiredVAs,
      filteredForms: searchedForms
    };
  }, [searchQuery, jobs, applications, hiredVAs, forms]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Escape key to clear search
      if (event.key === 'Escape' && searchQuery) {
        setSearchQuery('');
      }
      // Ctrl/Cmd + K to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  const loadDashboardData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');

      // Check cache first unless force refresh is requested
      if (!forceRefresh) {
        const cached = await cacheService.getDashboardStats(user.id, 'employer');
        if (cached) {
          console.log('📦 Dashboard stats from cache');
          setJobs(cached.jobs || []);
          setApplications(cached.applications || []);
          setHiredVAs(cached.hiredVAs || []);
          setForms(cached.forms || []);
          setAnalytics(cached.analytics || null);
          setLoadingTabs({
            jobs: false,
            applications: false,
            hired: false,
            forms: false,
            analytics: false
          });
          setLoading(false);
          return;
        }
      }

      console.log('💾 Loading dashboard data...');

      // Load only active tab immediately, load others in background
      const tabsToLoad = {
        jobs: activeTab === 'jobs' || !forceRefresh,
        applications: activeTab === 'applications' || !forceRefresh,
        hired: activeTab === 'hired' || !forceRefresh,
        forms: activeTab === 'forms' || !forceRefresh,
        analytics: activeTab === 'analytics' || !forceRefresh
      };

      // Load active tab data
      if (tabsToLoad.jobs && activeTab === 'jobs') {
        await loadTabData('jobs', forceRefresh);
      } else if (activeTab !== 'jobs') {
        setLoadingTabs(prev => ({ ...prev, jobs: true }));
      }

      if (tabsToLoad.applications && activeTab === 'applications') {
        await loadTabData('applications', forceRefresh);
      } else if (activeTab !== 'applications') {
        setLoadingTabs(prev => ({ ...prev, applications: true }));
      }

      if (tabsToLoad.hired && activeTab === 'hired') {
        await loadTabData('hired', forceRefresh);
      } else if (activeTab !== 'hired') {
        setLoadingTabs(prev => ({ ...prev, hired: true }));
      }

      if (tabsToLoad.forms && activeTab === 'forms') {
        await loadTabData('forms', forceRefresh);
      } else if (activeTab !== 'forms') {
        setLoadingTabs(prev => ({ ...prev, forms: true }));
      }

      if (tabsToLoad.analytics && activeTab === 'analytics') {
        await loadTabData('analytics', forceRefresh);
      } else if (activeTab !== 'analytics') {
        setLoadingTabs(prev => ({ ...prev, analytics: true }));
      }

      setLoading(false);
    } catch (err) {
      console.error('Dashboard loading error:', err);
      setError('Failed to load dashboard data. Please refresh the page.');
      setLoading(false);
    }
  };

  const loadTabData = async (tab, forceRefresh = false) => {
    try {
      let resultJobs, resultApplications, resultHired, resultForms, resultAnalytics;

      switch (tab) {
        case 'jobs': {
          setLoadingTabs(prev => ({ ...prev, jobs: true }));
          const jobsResult = await employerService.getEmployerJobs(user.id);
          if (jobsResult.success) {
            setJobs(jobsResult.data || []);
            resultJobs = jobsResult.data || [];
          } else {
            console.error('Failed to load jobs:', jobsResult.error);
            resultJobs = [];
          }
          setLoadingTabs(prev => ({ ...prev, jobs: false }));
          
          // Update cache with this tab's data
          if (!forceRefresh) {
            const cached = await cacheService.getDashboardStats(user.id, 'employer');
            if (cached) {
              await cacheService.setDashboardStats(user.id, 'employer', {
                ...cached,
                jobs: resultJobs
              });
            }
          }
          break;
        }

        case 'applications': {
          setLoadingTabs(prev => ({ ...prev, applications: true }));
          const applicationsResult = await employerService.getJobApplications(user.id);
          if (applicationsResult.success) {
            const transformedApplications = (applicationsResult.data || []).map(app => ({
              id: app.id,
              applicant_id: app.applicant_id,
              job_id: app.job_id,
              name: app.applicant?.full_name || 'Unknown',
              title: app.applicant?.bio?.split('.')[0] || 'Virtual Assistant',
              avatar: app.applicant?.profile_picture || null,
              matchPercentage: app.match_percentage || 0,
              rating: 4.5 + Math.random() * 0.5,
              experience: app.applicant?.experience_years || 0,
              hourlyRate: app.applicant?.hourly_rate || 0,
              proposedRate: app.proposed_rate || app.applicant?.hourly_rate || 0,
              coverLetter: app.cover_letter || '',
              skills: app.applicant?.skills || [],
              location: app.applicant?.location || 'Unknown',
              appliedAt: app.applied_at ? new Date(app.applied_at).toLocaleDateString() : 'Application Date Unknown',
              status: app.status,
              jobTitle: app.job?.title || 'Unknown Job',
              employerNotes: app.employer_notes || '',
              interviewScheduledAt: app.interview_scheduled_at || null,
              match_breakdown: app.match_breakdown || null
            }));
            setApplications(transformedApplications);
            resultApplications = transformedApplications;
          } else {
            console.error('Failed to load applications:', applicationsResult.error);
            resultApplications = [];
          }
          setLoadingTabs(prev => ({ ...prev, applications: false }));
          
          // Update cache
          if (!forceRefresh) {
            const cached = await cacheService.getDashboardStats(user.id, 'employer');
            if (cached) {
              await cacheService.setDashboardStats(user.id, 'employer', {
                ...cached,
                applications: resultApplications
              });
            }
          }
          break;
        }

        case 'hired': {
          setLoadingTabs(prev => ({ ...prev, hired: true }));
          const hiredResult = await getHiredVAs(user.id);
          if (hiredResult.success) {
            setHiredVAs(hiredResult.data || []);
            resultHired = hiredResult.data || [];
          } else {
            console.error('Failed to load hired VAs:', hiredResult.error);
            resultHired = [];
          }
          setLoadingTabs(prev => ({ ...prev, hired: false }));
          
          // Update cache
          if (!forceRefresh) {
            const cached = await cacheService.getDashboardStats(user.id, 'employer');
            if (cached) {
              await cacheService.setDashboardStats(user.id, 'employer', {
                ...cached,
                hiredVAs: resultHired
              });
            }
          }
          break;
        }

        case 'forms': {
          setLoadingTabs(prev => ({ ...prev, forms: true }));
          const formsResult = await formService.getEmployerForms(user.id);
          if (formsResult.success) {
            setForms(formsResult.data || []);
            resultForms = formsResult.data || [];
          } else {
            console.error('Failed to load forms:', formsResult.error);
            resultForms = [];
          }
          setLoadingTabs(prev => ({ ...prev, forms: false }));
          
          // Update cache
          if (!forceRefresh) {
            const cached = await cacheService.getDashboardStats(user.id, 'employer');
            if (cached) {
              await cacheService.setDashboardStats(user.id, 'employer', {
                ...cached,
                forms: resultForms
              });
            }
          }
          break;
        }

        case 'analytics': {
          setLoadingTabs(prev => ({ ...prev, analytics: true }));
          const analyticsResult = await employerService.getEmployerAnalytics(user.id);
          if (analyticsResult.success) {
            setAnalytics(analyticsResult.data);
            resultAnalytics = analyticsResult.data;
          } else {
            console.error('Failed to load analytics:', analyticsResult.error);
            resultAnalytics = null;
          }
          setLoadingTabs(prev => ({ ...prev, analytics: false }));
          
          // Update cache
          if (!forceRefresh) {
            const cached = await cacheService.getDashboardStats(user.id, 'employer');
            if (cached) {
              await cacheService.setDashboardStats(user.id, 'employer', {
                ...cached,
                analytics: resultAnalytics
              });
            }
          }
          break;
        }

        default:
          break;
      }
    } catch (err) {
      console.error(`Error loading ${tab}:`, err);
      setLoadingTabs(prev => ({ ...prev, [tab]: false }));
    }
  };

  const displayJobs = filteredJobs || [];
  const displayApplications = filteredApplications || [];
  const displayHiredVAs = filteredHiredVAs || [];
  const displayForms = filteredForms || [];

  const tabs = [
    { id: 'jobs', label: 'Active Jobs', icon: 'Briefcase', count: displayJobs.filter(job => job.status === 'published').length },
    { id: 'applications', label: 'Applications', icon: 'Users', count: displayApplications.length },
    { id: 'hired', label: 'Hired VAs', icon: 'UserCheck', count: displayHiredVAs.length },
    { id: 'forms', label: 'Forms', icon: 'FileText', count: displayForms.length },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  const handleJobAction = async (action, jobId, status = null) => {
    try {
      switch (action) {
        case 'edit': {
          // Use public_slug for cleaner URLs if available
          const jobItem = jobs.find(j => j.id === jobId);
          const urlParam = jobItem?.public_slug || jobId;
          navigate(`/job-posting/${urlParam}`);
          break;
        }
        case 'view-applications': 
          navigate(`/employer-dashboard?tab=applications&jobId=${jobId}`);
          break;
        case 'delete': {
          const result = await employerService.deleteJob(jobId, user.id);
          if (result.success) {
            await loadDashboardData(true); // Force refresh after delete
          } else {
            setError(new SupabaseError(result.error || 'Failed to delete job').getUserFriendlyMessage() || 'Failed to delete job');
          }
          break;
        }
        case 'duplicate':
          { const duplicateResult = await employerService.duplicateJob(jobId, user.id);
          if (duplicateResult.success) {
            await loadDashboardData(true); // Force refresh after duplicate
            // Optionally navigate to edit the new job
            if (duplicateResult.data?.id) {
              const urlParam = duplicateResult.data.public_slug || duplicateResult.data.id;
              navigate(`/job-posting/${urlParam}`);
            }
          } else {
            setError(new SupabaseError(duplicateResult.error || 'Failed to duplicate job').getUserFriendlyMessage() || 'Failed to duplicate job');
          }
          break; }
        case 'toggle-status':
          { const statusResult = await employerService.updateJobStatus(jobId, status, user.id);
          if (statusResult.success) {
            await loadDashboardData(true); // Force refresh after status change
          } else {
            setError(new SupabaseError(statusResult.error || 'Failed to update job status').getUserFriendlyMessage() || 'Failed to update job status');
          }
          break; }
        case 'archive':
          { const archiveResult = await employerService.updateJobStatus(jobId, 'archived', user.id);
          if (archiveResult.success) {
            await loadDashboardData(true); // Force refresh after archive
          } else {
            setError(new SupabaseError(archiveResult.error || 'Failed to archive job').getUserFriendlyMessage() || 'Failed to archive job');
          }
          break; }
        case 'refresh':
          { const refreshResult = await employerService.refreshJobPosting(jobId, user.id);
          if (refreshResult.success) {
            await loadDashboardData(true); // Force refresh after job refresh
          } else {
            setError(new SupabaseError(refreshResult.error || 'Failed to refresh job posting').getUserFriendlyMessage() || 'Failed to refresh job posting');
          }
          break; }
        case 'create':
          navigate('/job-posting');
          break;
        default:
          console.log(`${action} job:`, jobId);
      }
    } catch (err) {
      console.error('Job action error:', err);
      setError('Failed to perform action. Please try again.');
    }
  };

  const handleApplicationAction = async (action, applicationId) => {
    try {
      if (!employerService || typeof employerService.updateApplicationStatus !== 'function') {
        setError('Service unavailable. Please refresh the page and try again.');
        return;
      }

      // Check subscription limits for hiring actions
      if (action === 'accept') {
        // Check monthly hire limit
        const hireLimits = await canPerformAction('hires');
        if (!hireLimits.allowed) {
          setError(`You've reached your monthly hiring limit. Upgrade your plan to hire more VAs. Remaining: ${hireLimits.remaining}`);
          return;
        }

        // Check lifetime hire limit
        const maxHireLimits = await canPerformAction('max_hires');
        if (!maxHireLimits.allowed) {
          setError(`You've reached your total hiring limit. Upgrade your plan to hire more VAs. Remaining: ${maxHireLimits.remaining}`);
          return;
        }
      }

      switch (action) {
        case 'accept':
          { const acceptResult = await employerService.updateApplicationStatus(applicationId, 'accepted');
          if (acceptResult.success) {
            // Record hire action for subscription usage
            await recordAction('hire');
            await loadDashboardData(true); // Force refresh after accept
          } else {
            setError(new SupabaseError(acceptResult.error || 'Failed to accept application').getUserFriendlyMessage() || 'Failed to accept application');
          }
          break; }
        case 'reject':
          { const rejectResult = await employerService.updateApplicationStatus(applicationId, 'rejected');
          if (rejectResult.success) {
            await loadDashboardData(true); // Force refresh after reject
          } else {
            setError(new SupabaseError(rejectResult.error || 'Failed to reject application').getUserFriendlyMessage() || 'Failed to reject application');
          }
          break; }
        case 'message':
          // Get the applicant ID from the application
          { const application = applications.find(app => app.id === applicationId);
          if (application && application.applicant_id) {
            navigate(`/messages-communication?va=${application.applicant_id}`);
          } else {
            navigate(`/messages-communication?candidate=${applicationId}`);
          }
          break; }
        case 'shortlist': {
          const shortlistResult = await employerService.updateApplicationStatus(applicationId, 'shortlisted');
          if (shortlistResult.success) {
            await loadDashboardData(true); // Force refresh after shortlist
          } else {
            setError(new SupabaseError(shortlistResult.error || 'Failed to shortlist application').getUserFriendlyMessage() || 'Failed to shortlist application');
          }
          break;
        }
        case 'schedule-interview': {
          // Find the application to get candidate details
          const application = applications.find(app => app.id === applicationId);
          if (application) {
            setSelectedApplicationForInterview(application);
            setShowInterviewModal(true);
          } else {
            setError('Application not found. Please refresh and try again.');
          }
          break;
        }
        default:
          console.log(`${action} application:`, applicationId);
      }
    } catch (err) {
      console.error('Application action error:', err);
      setError('Failed to perform action. Please try again.');
    }
  };

  const handleVAAction = async (action, vaIdOrObject) => {
    try {
      switch (action) {
        case 'message': {
          // For message and view-profile, we still expect just the ID
          const vaId = typeof vaIdOrObject === 'string' ? vaIdOrObject : vaIdOrObject.va_id;
          navigate(`/messages-communication?va=${vaId}`);
          break;
        }
        case 'view-profile': {
          const profileId = typeof vaIdOrObject === 'string' ? vaIdOrObject : vaIdOrObject.va_id;
          navigate(`/va-profile/${profileId}`);
          break;
        }
        case 'end-contract': {
          // For end-contract, we expect the full VA object with contract_id
          if (typeof vaIdOrObject === 'object' && vaIdOrObject.contract_id) {
            const result = await endContract(vaIdOrObject.contract_id, 'Contract ended by employer');
            if (result.success) {
              await loadDashboardData(true); // Force refresh after ending contract
            } else {
              setError(new SupabaseError(result.error || 'Failed to end contract').getUserFriendlyMessage() || 'Failed to end contract');
            }
          } else {
            setError('No active contract found for this VA');
          }
          break;
        }
        default:
          console.log(`${action} VA:`, vaIdOrObject);
      }
    } catch (err) {
      console.error('VA action error:', err);
      setError('Failed to perform action. Please try again.');
    }
  };

  const handleFormAction = async (action, formId) => {
    try {
      switch (action) {
        case 'edit':
          navigate(`/form-builder/${formId}`);
          break;
        case 'delete': {
          const deleteResult = await formService.deleteForm(formId, user.id);
          if (deleteResult.success) {
            await loadDashboardData(true); // Force refresh after delete
          } else {
            setError(new SupabaseError(deleteResult.error || 'Failed to delete form').getUserFriendlyMessage() || 'Failed to delete form');
          }
          break;
        }
        case 'duplicate': {
          const duplicateResult = await formService.duplicateForm(formId, user.id);
          if (duplicateResult.success) {
            await loadDashboardData(true); // Force refresh after duplicate
            // Optionally navigate to edit the new form
            if (duplicateResult.data?.id) {
              navigate(`/form-builder/${duplicateResult.data.id}`);
            }
          } else {
            setError(new SupabaseError(duplicateResult.error || 'Failed to duplicate form').getUserFriendlyMessage() || 'Failed to duplicate form');
          }
          break;
        }
        case 'publish': {
          const publishResult = await formService.publishForm(formId, user.id);
          if (publishResult.success) {
            await loadDashboardData(true); // Force refresh after publish
          } else {
            setError(new SupabaseError(publishResult.error || 'Failed to publish form').getUserFriendlyMessage() || 'Failed to publish form');
          }
          break;
        }
        case 'unpublish': {
          const unpublishResult = await formService.unpublishForm(formId, user.id);
          if (unpublishResult.success) {
            await loadDashboardData(true); // Force refresh after unpublish
          } else {
            setError(new SupabaseError(unpublishResult.error || 'Failed to unpublish form').getUserFriendlyMessage() || 'Failed to unpublish form');
          }
          break;
        }
        case 'create':
          navigate('/form-builder');
          break;
        default:
          console.log(`${action} form:`, formId);
      }
    } catch (err) {
      console.error('Form action error:', err);
      setError('Failed to perform action. Please try again.');
    }
  };

const handlePostJob = (_jobData) => {
navigate('/job-posting');
};

const handleTeamManagement = () => {
navigate('/team-management');
};

const handleAdvancedAnalytics = () => {
navigate('/advanced-analytics');
};

const handleAIMatching = () => {
navigate('/auto-matching');
};

const handleEnterpriseFeatures = () => {
navigate('/enterprise-features');
};

  const handleSearchCandidates = () => {
    // Navigate to candidate search page or open search modal
    console.log('Search candidates functionality');
    // TODO: Implement candidate search page
    navigate('/candidate-search');
  };

  const handleScheduleInterview = () => {
    // Navigate to interview management page for employers
    console.log('Schedule interview functionality');
    // Navigate to employer interview management page
    navigate('/interview-management');
  };  const handleInterviewScheduled = async (interview) => {
    try {
      const app = interview.selectedApplication;
      if (!app) {
        setError('No candidate selected. Please select a candidate before scheduling.');
        return;
      }

      const { error: createError } = await supabaseService.interviews.createInterview({
        scheduled_at: interview.scheduled_at,
        duration: interview.duration,
        type: interview.interview_type,
        meeting_link: interview.meeting_link || null,
        notes: interview.notes || null,
        candidate_id: app.applicant_id,
        application_id: app.id,
        job_id: app.job_id,
        interviewer_id: interview.interviewer_id,
        time_slot_id: interview.time_slot_id || null,
        status: 'scheduled',
      });

      if (createError) throw new Error(createError);

      setShowInterviewModal(false);
      setSelectedApplicationForInterview(null);
      await loadDashboardData(true);
    } catch (error) {
      console.error('Error scheduling interview:', error);
      setError(error.message || 'Failed to schedule interview. Please try again.');
    }
  };

  const renderTabContent = () => {
    // Determine view mode based on sidebar state
    const viewMode = sidebarCollapsed ? 'grid' : 'table';
    
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-text-secondary">Loading...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Error Loading Data</h3>
          <p className="text-text-secondary mb-4">{error}</p>
          <Button variant="primary" onClick={loadDashboardData}>
            Try Again
          </Button>
        </div>
      );
    }

    // Check if search is active and no results found
    const isSearchActive = searchQuery.trim() !== '';
    const hasNoResults = isSearchActive && (
      (activeTab === 'jobs' && displayJobs.length === 0) ||
      (activeTab === 'applications' && displayApplications.length === 0) ||
      (activeTab === 'hired' && displayHiredVAs.length === 0) ||
      (activeTab === 'forms' && displayForms.length === 0)
    );

    if (hasNoResults) {
      return (
        <div className="text-center py-12">
          <Icon name="SearchX" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">No results found</h3>
          <p className="text-text-secondary mb-4">
            No {activeTab} found matching "{searchQuery}". Try adjusting your search terms.
          </p>
          <Button variant="outline" onClick={() => setSearchQuery('')}>
            Clear Search
          </Button>
        </div>
      );
    }

    switch (activeTab) {
      case 'jobs':
        if (loadingTabs.jobs) {
          return <JobsTableSkeleton viewMode={viewMode} />;
        }
        return (
          <JobsTable
            jobs={displayJobs}
            onEditJob={(id) => handleJobAction('edit', id)}
            onViewApplications={(id) => handleJobAction('view-applications', id)}
            onDeleteJob={(id) => handleJobAction('delete', id)}
            onDuplicateJob={(id) => handleJobAction('duplicate', id)}
            onToggleStatus={(id, status) => handleJobAction('toggle-status', id, status)}
            onRefreshPosting={(id) => handleJobAction('refresh', id)}
            onCreateJob={() => handleJobAction('create')}
            loading={loading}
            viewMode={viewMode}
          />
        );
      case 'applications':
        if (loadingTabs.applications) {
          return <ApplicationsGridSkeleton viewMode={viewMode} />;
        }
        return (
          <ApplicationsGrid
            applications={displayApplications}
            jobFilter={jobFilter}
            onAcceptApplication={(id) => handleApplicationAction('accept', id)}
            onRejectApplication={(id) => handleApplicationAction('reject', id)}
            onMessageCandidate={(id) => handleApplicationAction('message', id)}
            onScheduleInterview={(id) => handleApplicationAction('schedule-interview', id)}
            onViewProfile={(id) => handleApplicationAction('view-profile', id)}
            onRefresh={loadDashboardData}
            viewMode={viewMode}
          />
        );
      case 'hired':
        if (loadingTabs.hired) {
          return <HiredVAsListSkeleton viewMode={viewMode} />;
        }
        return (
          <HiredVAsList
            hiredVAs={displayHiredVAs}
            onMessageVA={(id) => handleVAAction('message', id)}
            onViewProfile={(id) => handleVAAction('view-profile', id)}
            onEndContract={(va) => handleVAAction('end-contract', va)}
            onRefresh={loadDashboardData}
            isLoading={loading}
            viewMode={viewMode}
          />
        );
      case 'forms':
        if (loadingTabs.forms) {
          return <FormsTableSkeleton viewMode={viewMode} />;
        }
        return (
          <FormsTable
            forms={displayForms}
            onEditForm={(id) => handleFormAction('edit', id)}
            onDeleteForm={(id) => handleFormAction('delete', id)}
            onDuplicateForm={(id) => handleFormAction('duplicate', id)}
            onPublishForm={(id) => handleFormAction('publish', id)}
            onUnpublishForm={(id) => handleFormAction('unpublish', id)}
            onCreateForm={() => handleFormAction('create')}
            loading={loading}
            viewMode={viewMode}
          />
        );
      case 'analytics':
        if (loadingTabs.analytics) {
          return <PerformanceAnalyticsSkeleton />;
        }
        return <PerformanceAnalytics analytics={analytics} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <EmployerHeaderNav />
      <main className="content-container pt-16 pb-20 md:pb-8">
    
        <div className="container">
          {/* Dashboard Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Welcome back, {userProfile?.full_name || 'Employer'}
                </h1>
                <p className="text-text-secondary mt-1">Manage your job postings, applications, and hired VAs</p>
              </div>
              
              
            </div>
          </div>

          

          <div className={`grid gap-8 ${sidebarCollapsed ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-12'}`}>
            {/* Main Content Area */}
            <div className={sidebarCollapsed ? 'col-span-1' : 'xl:col-span-8'}>
              {/* Tab Navigation - Hidden on mobile */}
              <div className="bg-surface rounded-lg border border-border mb-6 hidden md:block">
                <div className="flex overflow-x-auto items-center">
                  <div className="flex overflow-x-auto flex-1">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => navigate(`/employer-dashboard?tab=${tab.id}`)}
                        className={`flex items-center space-x-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors duration-200 whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'border-primary text-primary bg-primary-50' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                        }`}
                      >
                        <Icon name={tab.icon} size={18} />
                        <span>{tab.label}</span>
                        {tab.count !== undefined && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            activeTab === tab.id
                              ? 'bg-primary text-white' :'bg-secondary-100 text-text-secondary'
                          }`}>
                            {searchQuery ? (
                              activeTab === 'jobs' ? displayJobs.length :
                              activeTab === 'applications' ? displayApplications.length :
                              activeTab === 'hired' ? displayHiredVAs.length :
                              activeTab === 'forms' ? displayForms.length : tab.count
                            ) : tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Sidebar Toggle Button */}
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    className="p-2 ml-2 text-text-secondary hover:bg-secondary-100 rounded transition-colors hidden xl:block"
                    title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                  >
                    <Icon name={sidebarCollapsed ? 'ChevronLeft' : 'ChevronRight'} size={18} />
                  </button>
                </div>

                {/* Search Results Indicator */}
                {searchQuery && (
                  <div className="px-6 py-3 bg-primary-50 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-primary">
                        <Icon name="Search" size={16} />
                        <span>
                          Search results for "{searchQuery}" - 
                          {activeTab === 'jobs' && ` ${displayJobs.length} job${displayJobs.length !== 1 ? 's' : ''} found`}
                          {activeTab === 'applications' && ` ${displayApplications.length} application${displayApplications.length !== 1 ? 's' : ''} found`}
                          {activeTab === 'hired' && ` ${displayHiredVAs.length} VA${displayHiredVAs.length !== 1 ? 's' : ''} found`}
                          {activeTab === 'forms' && ` ${displayForms.length} form${displayForms.length !== 1 ? 's' : ''} found`}
                          {activeTab === 'analytics' && ' (search not available in analytics)'}
                        </span>
                      </div>
                      <button
                        onClick={() => setSearchQuery('')}
                        className="text-sm text-primary hover:text-primary-dark underline"
                      >
                        Clear search
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Tab Dropdown (visible on small screens) */}
              <div className="mb-4 md:hidden">
                <h3 className="text-sm font-medium text-text-secondary mb-2">View Dashboard Section:</h3>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    {tabs.find(tab => tab.id === activeTab)?.icon && (
                      <Icon name={tabs.find(tab => tab.id === activeTab).icon} size={20} className="text-primary" />
                    )}
                  </div>
                  <select
                    value={activeTab}
                    onChange={(e) => {
                      const newTab = e.target.value;
                      // Preserve jobId filter if it exists and we're going to applications tab
                      const jobIdParam = (newTab === 'applications' && jobFilter) ? `&jobId=${jobFilter}` : '';
                      navigate(`/employer-dashboard?tab=${newTab}${jobIdParam}`);
                    }}
                    className="w-full appearance-none px-4 py-3 bg-surface border border-border rounded-lg text-text-primary font-medium focus:ring-2 focus:ring-primary focus:border-primary pl-10"
                    style={{ backgroundImage: 'none' }} /* Remove default arrow */
                  >
                    {tabs.map((tab) => (
                      <option key={tab.id} value={tab.id} className="w-80">
                        {tab.label} {tab.count !== undefined ? `(${tab.count})` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                    <Icon name="ChevronDown" size={20} className="text-text-secondary" />
                  </div>
                </div>
                <div className="mt-4 border-t border-border pt-4 md:hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-text-primary">
                        {tabs.find(tab => tab.id === activeTab)?.label} Content
                      </span>
                      {activeTab === 'applications' && jobFilter && (
                        <div className="text-xs text-primary mt-1 flex items-center">
                          <Icon name="Filter" size={12} className="mr-1" />
                          <span>Filtered by job</span>
                        </div>
                      )}
                    </div>
                    
                    {activeTab === 'hired' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RefreshCw"
                        onClick={loadDashboardData}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                    )}
                    {activeTab === 'analytics' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="BarChart2"
                        onClick={handleAdvancedAnalytics}
                      >
                        Advanced
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Tab Content */}
              <div className="min-h-96">
                {renderTabContent()}
              </div>
            </div>

            {/* Sidebar */}
            {!sidebarCollapsed && (
              <div className="xl:col-span-4 space-y-6">
                <QuickActions
                  onPostJob={handlePostJob}
                  onSearchCandidates={handleSearchCandidates}
                  onScheduleInterview={handleScheduleInterview}
                  onAIMatching={handleAIMatching}
                  onAdvancedAnalytics={handleAdvancedAnalytics}
                  onEnterpriseFeatures={handleEnterpriseFeatures}
                  onTeamManagement={handleTeamManagement}
                  onMessaging={() => navigate('/messages')}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Interview Schedule Modal */}
      {showInterviewModal && selectedApplicationForInterview && (
        <EnhancedInterviewScheduleModal
          isOpen={showInterviewModal}
          onClose={() => {
            setShowInterviewModal(false);
            setSelectedApplicationForInterview(null);
          }}
          onSubmit={handleInterviewScheduled}
          selectedCandidate={{
            id: selectedApplicationForInterview.applicant_id,
            name: selectedApplicationForInterview.name,
            job_title: selectedApplicationForInterview.jobTitle
          }}
          initialData={{
            jobId: selectedApplicationForInterview.job_id,
            job_title: selectedApplicationForInterview.jobTitle,
            applicationId: selectedApplicationForInterview.id
          }}
          currentUserId={user?.id}
        />
      )}
</div>
  );
};

export default EmployerDashboard;

